// Lib imports
import React, { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

// utils
import isEmpty from 'lodash/isEmpty';
import each from 'lodash/each';
import omit from 'lodash/omit';
import {
  paywallHeadScriptV2,
  paywallHeadScriptV3,
  getPaywallBodyScriptCode,
} from 'pages/MerchantPages/Assets/components/Navbar/modals/utils/EmbedCodes';

// Constants
import { SUB_DOMAIN_URL } from 'constants/index';

// Types
import { PerAssetEmbedCodeOptions, GlobalEmbedCodeOptions } from 'ducks/merchantDucks/embedding/types';

// Actions
import { loadApplications } from 'ducks/merchantDucks/oauth/actions';

// Selectors
import {
  getGlobalOption,
  getEmbedCodeGlobalOptions,
  getEmbedCodeOptionsPerAsset,
} from 'ducks/merchantDucks/embedding/selectors';
import { getItemDetails } from 'ducks/merchantDucks/items/selectors';
import { getAuthState } from 'ducks/merchantDucks/user/selectors';

// styled components
import { BlueTypography, ItalicDescription } from 'components/SharedStyledElements';

// Components
import { Grid, Option, Colors, Dropdown } from '@inplayer-org/inplayer-ui';
import ClipBoardButton from 'components/ClipBoardButton';

const { Container, Cell } = Grid;

const EmbedCodeArea = styled(Container)`
  border: 1px solid ${Colors.gray};
  border-radius: 3px;
  background: ${Colors.lightGray};
  padding: 1rem;
  box-sizing: border-box;
`;

type CodePreProps = {
  height: number;
};

const CodePre = styled.pre`
  font-size: ${({ theme }) => theme.font.sizes.small};
  overflow-y: scroll;
  margin-bottom: 1rem;
  height: ${({ height }: CodePreProps) => `${height}rem`};
  font-family: monospace;
`;

const StyledContainer = styled(Container)`
  margin-top: 2rem;
`;

const ScriptTitleWrapper = styled(Cell)`
  display: flex;
  justify-content: space-between;
`;

const TitleDescription = styled(BlueTypography)`
  /* CS team requires this font size */
  font-size: 19px;
`;

const paywallVersions = {
  V2: 'v2',
  V3: 'v3',
};

const dropdownOptions: Array<Option> = [
  { value: paywallVersions.V2, displayName: 'Paywall V2' },
  { value: paywallVersions.V3, displayName: 'Paywall V3' },
];

const EmbedCode: FC = () => {
  const dispatch = useDispatch();
  // State
  const [paywallScriptVersion, setPaywallScriptVersion] = useState('v3');

  // Selectors
  const { id: assetId } = useSelector(getItemDetails);
  const { sub_domain_name: subdomainName } = useSelector(getAuthState);
  const useSSO = useSelector(getGlobalOption(GlobalEmbedCodeOptions.USE_SSO));
  const globalEmbedCodeObject = useSelector(getEmbedCodeGlobalOptions);
  const perAssetEmbedCodeObject = useSelector(getEmbedCodeOptionsPerAsset);
  const merchantUuid = useSelector(getItemDetails).merchant_uuid;

  useEffect(() => {
    dispatch(loadApplications());
  }, []);

  const isPaywallV2 = paywallScriptVersion === paywallVersions.V2;

  const generatePaywallBodyScriptCode = () => {
    const ssoDomain = useSSO ? `https://${subdomainName}.${SUB_DOMAIN_URL}` : '';

    const options: any = {};
    const globalOptions: any = {};

    each({ ...omit(perAssetEmbedCodeObject, PerAssetEmbedCodeOptions.ASSET_ID) }, (option, key) => {
      if (option) {
        if (key === PerAssetEmbedCodeOptions.PLAYER_SCRIPTS) {
          const newPlayerScripts = option.map((script: Record<string, any>) => omit(script, 'localId'));
          if (newPlayerScripts.length) {
            options[key] = newPlayerScripts;
          }
          return;
        }

        if (key === PerAssetEmbedCodeOptions.PLAYER_PARAMS) {
          const newPlayerParams = (option as Array<{ key: string; value: string }>).reduce(
            (acc, param) => ({ ...acc, [param.key]: param.value }),
            {}
          );

          if (!isEmpty(newPlayerParams)) {
            options[key] = newPlayerParams;
          }
          return;
        }
        if (Array.isArray(option) && !option.length) {
          return;
        }

        options[key] = option;
      }
    });

    const reducedGlobalEmbedCodeObject = isPaywallV2
      ? omit(globalEmbedCodeObject, GlobalEmbedCodeOptions.USER_MENU_POSITION)
      : globalEmbedCodeObject;

    each({ ...omit(reducedGlobalEmbedCodeObject, GlobalEmbedCodeOptions.USE_SSO), ssoDomain }, (option: any, key) => {
      const defaultTrueValues = [GlobalEmbedCodeOptions.ALLOW_DESTROY, GlobalEmbedCodeOptions.ALLOW_REMOVE];
      if (option && !defaultTrueValues.includes(key as GlobalEmbedCodeOptions)) {
        if (key === GlobalEmbedCodeOptions.FOOTER_LINKS) {
          const newFooterLinks = option.map((link: Record<string, any>) => omit(link, 'id'));
          if (newFooterLinks.length) {
            globalOptions[key] = newFooterLinks;
          }
          return;
        }

        if (Array.isArray(option) && !option.length) {
          return;
        }

        globalOptions[key] = option;
      }

      if (option === false && defaultTrueValues.includes(key as GlobalEmbedCodeOptions)) {
        globalOptions[key] = option;
      }
    });

    return getPaywallBodyScriptCode(merchantUuid, assetId, options, globalOptions);
  };

  // Generate body section
  const paywallBodyScriptCode = generatePaywallBodyScriptCode();

  const handleScriptVersionChange = (value: string) => {
    setPaywallScriptVersion(value);
  };

  return (
    <StyledContainer columns="20% 25% 1fr 1fr" rowGap="1rem">
      <Cell middle>
        <Dropdown
          tag="dropdown_paywall"
          value={paywallScriptVersion}
          options={dropdownOptions}
          onChange={handleScriptVersionChange}
        />
      </Cell>
      <ScriptTitleWrapper width={4}>
        <BlueTypography variant="h5">{isPaywallV2 ? 'Script V2' : 'Script V3'}</BlueTypography>
        <TitleDescription variant="h6">goes in the {'<head>'} of your webpage</TitleDescription>
      </ScriptTitleWrapper>
      <Cell width={4}>
        <EmbedCodeArea columns={4} rows="1fr 40px">
          <Cell width={4}>
            <CodePre height={2}>{isPaywallV2 ? paywallHeadScriptV2 : paywallHeadScriptV3}</CodePre>
          </Cell>
          <Cell left={1}>
            <ClipBoardButton
              tag={isPaywallV2 ? 'script_copy_inplayer_scripts_v2' : 'script_copy_inplayer_scripts_v3'}
              fullWidth
              fullHeight
              size="sm"
              clipboardText={isPaywallV2 ? paywallHeadScriptV2 : paywallHeadScriptV3}
              tooltipText="Copied!"
            >
              Copy InPlayer scripts
            </ClipBoardButton>
          </Cell>
        </EmbedCodeArea>
      </Cell>
      {isPaywallV2 && (
        <Cell width={4}>
          <ItalicDescription variant="p">
            You have selected an older paywall version that will get deprecated soon. We recommend you use Paywall v3.
          </ItalicDescription>
        </Cell>
      )}
      <ScriptTitleWrapper width={4}>
        <BlueTypography variant="h5">Embed code</BlueTypography>
        <TitleDescription variant="h6">goes in the {'<body>'} of your webpage</TitleDescription>
      </ScriptTitleWrapper>
      <Cell width={4}>
        <EmbedCodeArea columns={4} rows="1fr 40px">
          <Cell width={4}>
            <CodePre height={7}>{paywallBodyScriptCode}</CodePre>
          </Cell>
          <Cell left={1}>
            <ClipBoardButton
              tag={isPaywallV2 ? 'button_embed_code_copy_embed_code_v2' : 'button_embed_code_copy_embed_code_v3'}
              fullWidth
              fullHeight
              size="sm"
              clipboardText={paywallBodyScriptCode}
              tooltipText="Copied!"
            >
              Copy embed code
            </ClipBoardButton>
          </Cell>
        </EmbedCodeArea>
      </Cell>
    </StyledContainer>
  );
};

export default EmbedCode;
